import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Avatar,
    Chip,
    IconButton,
    Divider,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckIcon,
    PersonAdd as PersonAddIcon,
    Block as SkipIcon,
} from '@mui/icons-material';
import {
    CollegeRosterMatch,
    CollegeRosterUnmatched,
    confirmCollegeRoster,
    ConfirmCollegeRosterPlayer,
} from 'api/BaseballRecruits';

interface CollegeRosterReviewProps {
    open: boolean;
    onClose: () => void;
    matched: CollegeRosterMatch[];
    unmatched: CollegeRosterUnmatched[];
    groupUuid: string;
    onConfirmed: () => void;
}

type PlayerAction = 'link' | 'create' | 'skip';

interface PlayerDecision {
    action: PlayerAction;
    individualUuid?: string;
}

const CollegeRosterReview: React.FC<CollegeRosterReviewProps> = ({
    open,
    onClose,
    matched,
    unmatched,
    groupUuid,
    onConfirmed,
}) => {
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState('');

    const allPlayers = useMemo(() => {
        const items: {
            player: CollegeRosterMatch['player'];
            defaultAction: PlayerAction;
            match?: CollegeRosterMatch['match'];
            potentialMatches: CollegeRosterMatch['potential_matches'];
        }[] = [];

        for (const m of matched) {
            items.push({
                player: m.player,
                defaultAction: 'link',
                match: m.match,
                potentialMatches: m.potential_matches,
            });
        }
        for (const u of unmatched) {
            items.push({
                player: u.player,
                defaultAction: 'create',
                potentialMatches: u.potential_matches,
            });
        }
        return items;
    }, [matched, unmatched]);

    const [decisions, setDecisions] = useState<Record<string, PlayerDecision>>(() => {
        const init: Record<string, PlayerDecision> = {};
        for (const item of allPlayers) {
            const key = `${item.player.first_name}_${item.player.last_name}`;
            init[key] = {
                action: item.defaultAction,
                individualUuid: item.match?.individual_uuid || undefined,
            };
        }
        return init;
    });

    const getKey = (p: CollegeRosterMatch['player']) => `${p.first_name}_${p.last_name}`;

    const setAction = (key: string, action: PlayerAction, individualUuid?: string) => {
        setDecisions((prev) => ({
            ...prev,
            [key]: { action, individualUuid },
        }));
    };

    const handleConfirm = async () => {
        setConfirming(true);
        setError('');
        try {
            const players: ConfirmCollegeRosterPlayer[] = allPlayers.map((item) => {
                const key = getKey(item.player);
                const decision = decisions[key];
                return {
                    first_name: item.player.first_name,
                    last_name: item.player.last_name,
                    action: decision?.action || 'skip',
                    individual_uuid: decision?.individualUuid,
                    photo_url: item.player.photo_url,
                    position: item.player.position,
                    jersey: item.player.jersey,
                    hometown: item.player.hometown,
                    high_school: item.player.high_school,
                    home_state: item.player.state,
                };
            });

            await confirmCollegeRoster(groupUuid, players);
            onConfirmed();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to confirm roster import');
        }
        setConfirming(false);
    };

    const createCount = Object.values(decisions).filter((d) => d.action === 'create').length;
    const linkCount = Object.values(decisions).filter((d) => d.action === 'link').length;
    const skipCount = Object.values(decisions).filter((d) => d.action === 'skip').length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Typography variant="h6">Review College Roster Import</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: 'error.main',
                            color: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Chip
                        label={`${linkCount} to link`}
                        color="success"
                        variant="outlined"
                        size="small"
                    />
                    <Chip
                        label={`${createCount} to create`}
                        color="primary"
                        variant="outlined"
                        size="small"
                    />
                    <Chip label={`${skipCount} to skip`} variant="outlined" size="small" />

                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PersonAddIcon />}
                            onClick={() => {
                                setDecisions((prev) => {
                                    const next = { ...prev };
                                    for (const item of allPlayers) {
                                        const key = getKey(item.player);
                                        next[key] = {
                                            action: 'create',
                                            individualUuid: undefined,
                                        };
                                    }
                                    return next;
                                });
                            }}
                        >
                            Create All
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<SkipIcon />}
                            onClick={() => {
                                setDecisions((prev) => {
                                    const next = { ...prev };
                                    for (const item of allPlayers) {
                                        const key = getKey(item.player);
                                        next[key] = {
                                            action: 'skip',
                                            individualUuid: undefined,
                                        };
                                    }
                                    return next;
                                });
                            }}
                        >
                            Skip All
                        </Button>
                    </Box>
                </Box>

                {allPlayers.map((item, idx) => {
                    const key = getKey(item.player);
                    const decision = decisions[key];

                    return (
                        <React.Fragment key={key}>
                            {idx > 0 && <Divider sx={{ my: 1 }} />}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 1,
                                }}
                            >
                                <Avatar
                                    src={item.player.photo_url || undefined}
                                    variant="rounded"
                                    sx={{ width: 40, height: 40 }}
                                >
                                    {item.player.first_name?.[0]}
                                    {item.player.last_name?.[0]}
                                </Avatar>

                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {item.player.name}
                                        {item.player.jersey && (
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {' '}
                                                #{item.player.jersey}
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {[
                                            item.player.position,
                                            item.player.year_class,
                                            item.player.hometown,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </Typography>
                                    {item.match && (
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                size="small"
                                                label={`Matched: ${
                                                    item.match.member_name
                                                } (${Math.round(item.match.score * 100)}%)`}
                                                color={
                                                    item.match.confidence === 'high'
                                                        ? 'success'
                                                        : 'warning'
                                                }
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem' }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                <Select
                                    size="small"
                                    value={decision?.action || 'skip'}
                                    onChange={(e) => {
                                        const action = e.target.value as PlayerAction;
                                        setAction(
                                            key,
                                            action,
                                            action === 'link'
                                                ? item.match?.individual_uuid || undefined
                                                : undefined,
                                        );
                                    }}
                                    sx={{ minWidth: 130 }}
                                >
                                    {item.match && (
                                        <MenuItem value="link">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <CheckIcon fontSize="small" color="success" />
                                                Link
                                            </Box>
                                        </MenuItem>
                                    )}
                                    <MenuItem value="create">
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <PersonAddIcon fontSize="small" color="primary" />
                                            Create
                                        </Box>
                                    </MenuItem>
                                    <MenuItem value="skip">
                                        <Box
                                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            <SkipIcon fontSize="small" />
                                            Skip
                                        </Box>
                                    </MenuItem>
                                </Select>
                            </Box>
                        </React.Fragment>
                    );
                })}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={confirming || (createCount === 0 && linkCount === 0)}
                    startIcon={confirming ? <CircularProgress size={18} /> : undefined}
                >
                    {confirming
                        ? 'Confirming...'
                        : `Confirm (${linkCount + createCount} player${
                              linkCount + createCount !== 1 ? 's' : ''
                          })`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CollegeRosterReview;
